import React from "react";
import { Link } from "react-router-dom";

const shellStyle = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 48%, #fff7ed 100%)",
  padding: "48px 0 72px",
};

const containerStyle = {
  width: "min(1100px, calc(100% - 32px))",
  margin: "0 auto",
  display: "grid",
  gap: "24px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 56%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "30px",
  padding: "32px",
  boxShadow: "0 30px 70px rgba(124, 45, 58, 0.2)",
};

const cardStyle = {
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "24px",
  padding: "24px",
  boxShadow: "0 18px 34px rgba(15, 23, 42, 0.06)",
};

const ctaLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "999px",
  textDecoration: "none",
  color: "#fff",
  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
  fontWeight: 700,
};

function PolicyPage({ title, subtitle, overview, sections = [], cta }) {
  return (
    <section style={shellStyle}>
      <div style={containerStyle}>
        <div style={heroStyle}>
          <p style={{ margin: "0 0 8px", opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "12px", fontWeight: 800 }}>
            Our Policy
          </p>
          <h1 style={{ margin: "0 0 12px", fontSize: "clamp(30px, 5vw, 52px)", lineHeight: 1.05 }}>{title}</h1>
          <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75, maxWidth: "800px" }}>{subtitle}</p>
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: "#4e1b24" }}>Overview</h3>
          <p style={{ marginBottom: cta ? "18px" : 0, color: "#475569", lineHeight: 1.85 }}>{overview}</p>
          {cta ? (
            <Link to={cta.path} style={ctaLinkStyle}>
              {cta.label}
              <i className="bi bi-arrow-right"></i>
            </Link>
          ) : null}
        </div>

        <div style={{ display: "grid", gap: "18px" }}>
          {sections.map((section) => (
            <div key={section.heading} style={cardStyle}>
              <h3 style={{ marginTop: 0, marginBottom: "10px", color: "#0f172a" }}>{section.heading}</h3>
              <p style={{ margin: 0, color: "#475569", lineHeight: 1.85 }}>{section.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const policyPages = [
  {
    path: "/our-policy/terms-and-conditions",
    navLabel: "Terms & Conditions",
    title: "Terms & Conditions",
    subtitle: "Website and educational service use are governed by these operating terms.",
    overview: "These terms define how students, visitors, and franchise partners may use the NIDCS website, services, forms, learning resources, and support channels.",
    sections: [
      { heading: "Website Use", body: "Users must submit correct information, use institute resources lawfully, and avoid misuse of verification, registration, and communication systems." },
      { heading: "Service Access", body: "Admissions, support, certificates, and franchise-related services are processed according to institute rules, operational approval, and document verification." },
    ],
  },
  {
    path: "/our-policy/privacy-policy",
    navLabel: "Privacy Policy",
    title: "Privacy Policy",
    subtitle: "Personal data protection for students, parents, and franchise partners.",
    overview: "NIDCS collects only the information required to manage admissions, communication, centre coordination, examination workflow, and certificate-related services.",
    sections: [
      { heading: "Data We Handle", body: "This may include names, phone numbers, email addresses, academic data, uploaded documents, and franchise communication records." },
      { heading: "Security Commitment", body: "Reasonable administrative and technical steps are taken to protect records from unauthorized access, misuse, or unnecessary disclosure." },
    ],
  },
  {
    path: "/our-policy/disclaimer",
    navLabel: "Disclaimer",
    title: "Disclaimer",
    subtitle: "Clarification on educational information, certification references, and public claims.",
    overview: "All content on this website is published for educational, institutional, and informational purposes. Users should verify important operational details directly with NIDCS when needed.",
    sections: [
      { heading: "Educational Content", body: "Course descriptions, training modules, admission guidance, and institute notices may change according to operational updates." },
      { heading: "Career Outcome Clarification", body: "Unless explicitly stated in official written communication, website content should not be interpreted as a guaranteed job placement commitment." },
    ],
  },
  {
    path: "/our-policy/franchise-policy",
    navLabel: "Franchise Policy",
    title: "Franchise Policy / Terms of Association",
    subtitle: "Guidelines for applying, operating, and maintaining a NIDCS franchise centre.",
    overview: "This policy explains eligibility, infrastructure expectations, operational discipline, reporting standards, and brand-use responsibilities for franchise partners.",
    sections: [
      { heading: "Operational Duty", body: "Each centre must follow institute-approved admission, branding, communication, and student-support processes." },
      { heading: "Association Terms", body: "Any fee, royalty, renewal, or operational requirement shall be governed by the institute's active written agreement and official communication." },
    ],
  },
  {
    path: "/our-policy/code-of-conduct",
    navLabel: "Code of Conduct",
    title: "Code of Conduct",
    subtitle: "Professional conduct guidelines for franchise partners and academic representatives.",
    overview: "Every partner and centre representative is expected to maintain respectful, transparent, and ethical communication with students, guardians, and the NIDCS administration.",
    sections: [
      { heading: "Student Interaction", body: "Students must be treated respectfully, fairly, and without misleading promises regarding fees, certification, or career outcome." },
      { heading: "Brand Responsibility", body: "Partners must protect the reputation of NIDCS through disciplined conduct, accurate communication, and responsible centre management." },
    ],
  },
  {
    path: "/our-policy/territorial-policy",
    navLabel: "Territorial Policy",
    title: "Territorial Policy",
    subtitle: "Regional centre planning and area allocation principles.",
    overview: "NIDCS may apply area-based operational review when approving new centres so that academic quality, student service, and brand consistency are maintained.",
    sections: [
      { heading: "Area Consideration", body: "Centre approval may depend on local demand, active centre presence, service overlap, and institute expansion strategy." },
      { heading: "Approval Basis", body: "No territory should be treated as automatically reserved unless such approval is granted in official written communication." },
    ],
  },
  {
    path: "/our-policy/refund-cancellation-policy",
    navLabel: "Refund & Cancellation",
    title: "Refund & Cancellation Policy",
    subtitle: "Admission cancellation and fee-refund clarity for enrolled applicants.",
    overview: "Refund treatment depends on admission stage, document submission, administrative processing, and the nature of fees already applied to institutional workflow.",
    sections: [
      { heading: "Cancellation Request", body: "Students or guardians should submit cancellation requests through the proper communication channel with supporting details." },
      { heading: "Refund Review", body: "Refund eligibility, if any, will be reviewed according to institute process, payment stage, and applicable administrative deductions." },
    ],
  },
  {
    path: "/our-policy/anti-ragging-student-conduct",
    navLabel: "Anti-Ragging / Conduct",
    title: "Anti-Ragging / Student Conduct Policy",
    subtitle: "Discipline, safety, and respectful academic culture inside institute-linked premises.",
    overview: "Students must maintain discipline, mutual respect, and safe behaviour in classrooms, exam settings, centre premises, and institute-related communication channels.",
    sections: [
      { heading: "Zero Tolerance", body: "Harassment, intimidation, abuse, ragging, or disruptive misconduct is not acceptable in any institute-linked environment." },
      { heading: "Student Responsibility", body: "Students should follow attendance, conduct, communication, and exam-related rules communicated by the institute or authorized centre." },
    ],
  },
  {
    path: "/our-policy/examination-certification-policy",
    navLabel: "Examination & Certification",
    title: "Examination & Certification Policy",
    subtitle: "Exam process, pass criteria, evaluation, and certificate issue workflow.",
    overview: "This policy outlines how examinations are conducted, how student performance is reviewed, and how marksheets or certificates are generated after approval.",
    sections: [
      { heading: "Exam Process", body: "Exam scheduling, question structure, marks entry, evaluation, and approval are managed according to the academic workflow approved by NIDCS." },
      { heading: "Certification Process", body: "Certificates and marksheets are issued after successful completion, verification, and required academic approval." },
    ],
  },
  {
    path: "/our-policy/certificate-verification",
    navLabel: "Certificate Verification",
    title: "Certificate Verification",
    subtitle: "Verify student records with roll number or institute reference.",
    overview: "Students and stakeholders may use the certificate verification workflow to confirm academic identity and certificate authenticity through the official NIDCS verification channel.",
    cta: { label: "Open Verification Page", path: "/student-verification" },
    sections: [
      { heading: "Verification Purpose", body: "This page helps validate student status, course identity, and certificate association through the official institute system." },
    ],
  },
  {
    path: "/our-policy/affiliation-accreditation",
    navLabel: "Affiliation & Accreditation",
    title: "Affiliation & Accreditation",
    subtitle: "Institutional identity, legal registrations, and recognition references.",
    overview: "This section presents the institute's affiliation-style identity details, recognition references, and public institutional documents that support transparency and trust.",
    cta: { label: "Open Affiliation Page", path: "/affiliation" },
    sections: [
      { heading: "Document Reference", body: "Legal, ISO, MSME, and institutional recognition details may be displayed here for public review and confidence-building." },
    ],
  },
  {
    path: "/our-policy/contact-us",
    navLabel: "Contact Us",
    title: "Contact Us",
    subtitle: "Reach the institute through phone, WhatsApp, email, and location support.",
    overview: "This section gives access to institute communication channels, centre support direction, and public contact details for admission or operational queries.",
    cta: { label: "Open Contact Page", path: "/contact" },
    sections: [
      { heading: "Support Access", body: "Users may reach out for admission help, student query support, centre communication, or official information clarification." },
    ],
  },
  {
    path: "/our-policy/grievance-redressal",
    navLabel: "Grievance Redressal",
    title: "Grievance Redressal / Complaint Cell",
    subtitle: "Support route for student, guardian, or partner complaints and operational concerns.",
    overview: "Students, guardians, and partners may raise complaints, reporting concerns, or service-related issues through the official institute communication process.",
    cta: { label: "Send a Complaint / Query", path: "/contact" },
    sections: [
      { heading: "Complaint Handling", body: "The institute reviews complaints on the basis of submitted details, supporting information, and available service records." },
      { heading: "Responsible Use", body: "Complaint submissions should be factual, respectful, and complete so the concern can be reviewed efficiently." },
    ],
  },
];

export function PolicyRoutePage({ page }) {
  return <PolicyPage {...page} />;
}
